import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Benefit } from 'src/models/Benefit/Benefit';
import { DiscountChip } from 'src/components/DiscountChip';
import sanitizeHtml from 'src/utils/sanitizeHtml';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Image = styled.div`
  background: ${p => p.theme.palette.gray[100]};
  position: relative;
  padding-top: 125%;

  img {
    position: absolute;
    top: 0;
    display: block;
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`;

const Title = styled.h3`
  font-weight: bold;
  font-size: 19px;
  line-height: 120%;
  color: ${p => p.theme.palette.text.high};
  margin: 0 0 4px;
`;

const ChipRow = styled.div`
  margin-top: 8px;
`;

const Description = styled.div`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 150%;
  color: ${p => p.theme.palette.text.low};

  p {
    margin: 0 0 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const SkeletonBlock = styled.div`
  background: ${p => p.theme.palette.gray[100]};
  border-radius: 4px;
`;

export const BenefitCard: React.FC<{
  benefit?: Benefit;
}> = ({ benefit }) => {
  if (!benefit) {
    return (
      <div style={{ height: '100%' }}>
        <SkeletonBlock style={{ paddingTop: '125%' }} />
        <Header>
          <SkeletonBlock style={{ height: 24, marginBottom: 8 }} />
        </Header>
      </div>
    );
  }

  return (
    <Card to={`/benefits/${benefit.id}`}>
      <Image>
        {benefit.image && <img src={benefit.image} alt="" />}
      </Image>
      <Header>
        <Title>{benefit.name}</Title>
        {benefit.discount && (
          <ChipRow>
            <DiscountChip label={benefit.discount} />
          </ChipRow>
        )}
        {benefit.description && (
          <Description
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(benefit.description, { stripLinks: true }),
            }}
          />
        )}
      </Header>
    </Card>
  );
};

export default BenefitCard;
